// client/src/api/auth/tokenRefresh.ts
//
// DIPENDENZE DIRETTE: solo `errors.ts` e `fetch` nativo.
// NON importa apiFetch → rompe il ciclo.
// Riceve `updateAccess` e `logout` tramite iniezione di store
// (passati come argomenti), non tramite import top-level.

import {
  isNetworkError,
  SessionExpiredError,
  NetworkError,
} from "../core/errors";
import { resolveApiBaseUrl } from "@/utils/apiBase";

// Vedi apiFetch.ts / utils/apiBase.ts per la motivazione del fallback.
const API_BASE: string = resolveApiBaseUrl();

export interface TokenRefreshResult {
  accessToken: string;
  user: unknown; // Sostituisci con il tipo `User` del tuo dominio
}

interface AuthStoreActions {
  updateAccess: (accessToken: string, user: unknown) => void;
  logout: () => void;
}

// Singleton promise per request deduplication (evita N refresh paralleli)
let refreshInFlight: Promise<TokenRefreshResult> | null = null;

/**
 * FIX REFRESH-RACE-LOGOUT (20 Set 2026): esegue UN SOLO refresh alla volta
 * per TUTTI i chiamanti (apiFetch via refreshSession, store via
 * forceRefreshToken/initSession). Prima le due implementazioni avevano
 * due dedup separati: al resume (visibilità/telefonata) partivano in
 * parallelo, il refresh vincitore ruotava il token e il perdente riceveva
 * 401 → logout ingiustificato. Condividendo il singleton la race sparisce.
 */
export function sharedRefresh(): Promise<TokenRefreshResult> {
  if (!refreshInFlight) {
    refreshInFlight = executeRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

// FIX REFRESH-BACKOFF: dopo un refresh fallito, non riprovare per 30s.
// Prima: ogni 401 scatenava un nuovo refresh (dedup si azzerava a ogni
// fallimento) -> migliaia di POST /api/auth/refresh -> publicAuthLimiter
// esaurito -> login bloccato con 429 anche per gli utenti.
let lastRefreshFailureAt = 0;
const REFRESH_BACKOFF_MS = 30_000;

// FIX HANG-PROOF (20 Set 2026): il refresh usava fetch nativo SENZA timeout.
// Su reti mobili instabili (handover 4G/5G dopo telefonata, WebView sospesa)
// la richiesta poteva restare appesa per MINUTI → UI bloccata su
// "Salvataggio…"/"Avvio…" e refresh mai completato. Con AbortController la
// richiesta fallisce in max REFRESH_TIMEOUT_MS e il chiamante gestisce
// l'errore (retry/backoff) invece di appendere l'UI.
const REFRESH_TIMEOUT_MS = 20_000;

/**
 * Esegue il refresh del token verso l'endpoint backend.
 * Usa `fetch` nativo — NON usa apiFetch — per evitare import ciclici.
 */
async function executeRefresh(): Promise<TokenRefreshResult> {
  let refreshRes: Response;

  const timeout = new AbortController();
  const timeoutId = setTimeout(
    () => timeout.abort(new DOMException("Refresh timeout", "TimeoutError")),
    REFRESH_TIMEOUT_MS,
  );

  try {
    refreshRes = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({}),
      signal: timeout.signal,
    });
  } catch (networkErr) {
    const msg = isNetworkError(networkErr)
      ? "Server non raggiungibile, riprova tra qualche secondo."
      : networkErr instanceof Error
        ? networkErr.message
        : "Network error";
    throw new NetworkError(msg, 0);
  } finally {
    clearTimeout(timeoutId);
  }

  if (!refreshRes.ok) {
    let errMsg = "Session expired";
    try {
      const body = (await refreshRes.json()) as { error?: string };
      if (body?.error) errMsg = body.error;
    } catch {
      // body non parsable: usa messaggio default
    }
    throw new SessionExpiredError(errMsg);
  }

  const data = (await refreshRes.json()) as TokenRefreshResult;

  // FIX #REFRESH-VALIDATE: il server potrebbe rispondere 200 con body malformato.
  // Se mancano accessToken o user, consideriamo il refresh fallito.
  if (!data?.accessToken || !data?.user) {
    throw new SessionExpiredError("Refresh token response malformed");
  }

  return data;
}

/**
 * Punto di accesso publico con deduplication.
 * Accetta le action dello store come parametri → nessun import top-level di Zustand.
 */
export async function refreshSession(
  storeActions: AuthStoreActions,
): Promise<TokenRefreshResult> {
  // Backoff: refresh appena fallito -> non riprovare (evita loop 401->refresh)
  if (Date.now() - lastRefreshFailureAt < REFRESH_BACKOFF_MS) {
    throw new NetworkError(
      "Refresh in backoff: riprova tra qualche secondo",
      0,
    );
  }

  try {
    const data = await sharedRefresh();
    // Aggiorna lo store solo dopo il successo del refresh
    storeActions.updateAccess(data.accessToken, data.user);
    lastRefreshFailureAt = 0;
    return data;
  } catch (err) {
    // FIX REFRESH-RACE-LOGOUT (20 Set 2026): se il refresh condiviso
    // fallisce con sessione scaduta, il cookie potrebbe essere stato
    // appena RUOTATO da un refresh parallelo vincitore — riprova UNA
    // volta dopo 1.5s prima di dichiarare la sessione morta.
    if (err instanceof SessionExpiredError) {
      await new Promise((r) => setTimeout(r, 1_500));
      try {
        const data = await sharedRefresh();
        storeActions.updateAccess(data.accessToken, data.user);
        lastRefreshFailureAt = 0;
        return data;
      } catch (retryErr) {
        lastRefreshFailureAt = Date.now();
        if (retryErr instanceof SessionExpiredError) {
          storeActions.logout();
        }
        throw retryErr;
      }
    }
    lastRefreshFailureAt = Date.now();
    throw err;
  }
}

/**
 * Solo per test: azzera lo stato di backoff e dedup tra un test e l'altro.
 * Il backoff (REFRESH_BACKOFF_MS) e `refreshInFlight` sono singleton di
 * modulo: senza reset, un test che fallisce il refresh fa entrare i test
 * successivi nel ramo "Refresh in backoff" (falsi negativi).
 */
export function _resetRefreshStateForTests(): void {
  lastRefreshFailureAt = 0;
  refreshInFlight = null;
}
